import type { Account, AccountLedgerEntry, BalanceAudit, Budget, CrDrEntry, PlanPayment, Transaction } from '../types';
import { displayDate, formatMoney } from './date';
import { colName, escapeXml, makeZip, workbookFiles } from './xlsxCore';
import { downloadFinancePdf, type FinancePdfInput, type PdfTable } from './pdf';

export type ExportReportData = {
  fromDate: string;
  toDate: string;
  accountFilter: string;
  generatedAt: string;
  income: number;
  expense: number;
  investment: number;
  balance: number;
  todaySpend: number;
  liquidBalance: number;
  creditOutstanding: number;
  availableCredit: number;
  paidTotal: number;
  cr: number;
  dr: number;
  crdrBalance: number;
  categoryExpense: Array<[string, number]>;
  paymentModeExpense: Array<[string, number]>;
  budgetUsage: Array<Budget & { spent: number; remaining: number; usagePct: number; state: string }>;
  activeAccounts: Account[];
  rangeTx: Transaction[];
  rangeLedger: AccountLedgerEntry[];
  transfers: AccountLedgerEntry[];
  cardPayments: AccountLedgerEntry[];
  rangePayments: PlanPayment[];
  rangeCrdr: CrDrEntry[];
  rangeAudits: BalanceAudit[];
  taxIncome: Array<[string, number]>;
  taxExpense: Array<[string, number]>;
  taxInvestment: Array<[string, number]>;
  taxIgnored: Array<[string, number]>;
  topExpenses: Transaction[];
  exportSections?: string[];
};

const inr = (value: number) => `INR ${Math.round(value || 0).toLocaleString('en-IN')}`;
const clean = (value: unknown) => String(value ?? '');
const downloadBlob = (filename: string, blob: Blob) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
    link.remove();
  }, 1000);
};

const csvEscape = (value: unknown) => `"${clean(value).replace(/"/g, '""')}"`;
const downloadCsv = (filename: string, rows: Array<Record<string, unknown>>) => {
  const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row))));
  const csv = [headers.join(','), ...rows.map((row) => headers.map((header) => csvEscape(row[header])).join(','))].join('\n');
  downloadBlob(filename, new Blob([csv], { type: 'text/csv' }));
};

export function exportReportCsvBundle(prefix: string, data: ExportReportData) {
  const include = sectionFilter(data);
  if (include('overview')) downloadCsv(`${prefix}-summary.csv`, [
    { metric: 'Date Range', value: `${data.fromDate} to ${data.toDate}` },
    { metric: 'Account Filter', value: data.accountFilter },
    { metric: 'Income', value: data.income },
    { metric: 'Expense', value: data.expense },
    { metric: 'Investment', value: data.investment },
    { metric: 'Net Balance', value: data.balance },
    { metric: 'Today Spend', value: data.todaySpend }
  ]);
  if (include('transactions')) downloadCsv(`${prefix}-transactions.csv`, data.rangeTx.map((row) => ({
    date: row.dateISO,
    type: row.type,
    category: row.category,
    amount: row.amount,
    paymentMode: row.paymentMode,
    account: row.accountName || 'Unassigned',
    taxTag: row.taxTag || '',
    note: row.note || '',
    status: row.status || 'active',
    transactionId: row.id
  })));
  if (include('accounts') || include('movements')) downloadCsv(`${prefix}-account-ledger.csv`, data.rangeLedger.map((row) => ({
    date: row.dateISO,
    accountId: row.accountId,
    type: row.type,
    direction: row.direction,
    amount: row.amount,
    relatedAccountId: row.relatedAccountId || '',
    note: row.note || '',
    reference: row.transactionId || row.transferId || row.cardPaymentId || row.planPaymentId || '',
    ledgerId: row.id
  })));
  if (include('tax')) downloadCsv(`${prefix}-tax-summary.csv`, taxSummaryRows(data).map((row) => ({ taxTag: row[0], income: row[1], expense: row[2], investment: row[3], net: row[4], count: row[5] })));
  if (include('accounts')) downloadCsv(`${prefix}-account-summary.csv`, data.activeAccounts.map(accountSummaryRow));
}

const styles = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="2"><numFmt numFmtId="164" formatCode="yyyy-mm-dd"/><numFmt numFmtId="165" formatCode="&quot;INR&quot; #,##0;[Red]&quot;INR&quot; -#,##0"/></numFmts>
  <fonts count="3"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/><color rgb="FFFFFFFF"/></font><font><b/><sz val="13"/><name val="Calibri"/><color rgb="FF0D7F73"/></font></fonts>
  <fills count="4"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF0D7F73"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFEAF8F6"/></patternFill></fill></fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="6"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/><xf numFmtId="0" fontId="2" fillId="3" borderId="0" xfId="0" applyFont="1" applyFill="1"/><xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/><xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/><xf numFmtId="0" fontId="0" fillId="3" borderId="0" xfId="0" applyFill="1"/></cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

const cell = (col: number, row: number, value: unknown, style = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return `<c r="${colName(col)}${row}" s="${style || 3}"><v>${value}</v></c>`;
  return `<c r="${colName(col)}${row}" t="inlineStr"${style ? ` s="${style}"` : ''}><is><t>${escapeXml(clean(value))}</t></is></c>`;
};

const worksheet = (headers: string[], rows: unknown[][], widths?: number[]) => {
  const cols = headers.map((_, index) => `<col min="${index + 1}" max="${index + 1}" width="${widths?.[index] || 18}" customWidth="1"/>`).join('');
  const body = [
    `<row r="1">${headers.map((header, index) => cell(index, 1, header, 1)).join('')}</row>`,
    ...rows.map((row, rowIndex) => `<row r="${rowIndex + 2}">${headers.map((_, index) => cell(index, rowIndex + 2, row[index] ?? '', typeof row[index] === 'number' ? 3 : 0)).join('')}</row>`)
  ].join('');
  const endCell = `${colName(Math.max(headers.length - 1, 0))}${Math.max(rows.length + 1, 1)}`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews><cols>${cols}</cols><sheetData>${body}</sheetData><autoFilter ref="A1:${endCell}"/></worksheet>`;
};

const summarySheet = (data: ExportReportData) => worksheet(['Section', 'Metric', 'Value'], [
  ['Report', 'Date Range', `${data.fromDate} to ${data.toDate}`],
  ['Report', 'Account Filter', data.accountFilter],
  ['Report', 'Generated', data.generatedAt],
  ['KPI', 'Income', data.income],
  ['KPI', 'Expense', data.expense],
  ['KPI', 'Investment', data.investment],
  ['KPI', 'Net Balance', data.balance],
  ['KPI', 'Today Spend', data.todaySpend],
  ['Insight', 'Credit Card Outstanding', data.creditOutstanding],
  ['Insight', 'Available Credit', data.availableCredit],
  ['Insight', 'CR/DR Net', data.crdrBalance]
], [18, 28, 32]);

const accountSummaryRow = (account: Account) => ({
  accountName: account.name,
  accountType: account.type,
  openingBalance: account.openingBalance,
  currentBalance: account.currentBalance,
  creditLimit: account.creditLimit || 0,
  availableLimit: account.availableLimit || 0,
  outstanding: account.type === 'credit_card' ? account.currentBalance : 0,
  status: account.status
});

const taxSummaryRows = (data: ExportReportData) => {
  const tags = new Map<string, { income: number; expense: number; investment: number; count: number }>();
  data.rangeTx.forEach((row) => {
    const tag = row.taxTag || 'Untagged';
    const current = tags.get(tag) || { income: 0, expense: 0, investment: 0, count: 0 };
    if (row.type === 'Income') current.income += row.amount;
    if (row.type === 'Expense') current.expense += row.amount;
    if (row.type === 'Investment') current.investment += row.amount;
    current.count += 1;
    tags.set(tag, current);
  });
  return Array.from(tags.entries()).map(([tag, value]) => [tag, value.income, value.expense, value.investment, value.income - value.expense - value.investment, value.count]);
};

const sectionFilter = (data: ExportReportData) => {
  const selected = new Set(data.exportSections || []);
  return (section: string) => !selected.size || selected.has(section);
};

const keepSheet = (data: ExportReportData, name: string) => {
  const include = sectionFilter(data);
  const map: Record<string, string[]> = {
    Summary: ['overview'],
    Transactions: ['transactions'],
    'Account Ledger': ['accounts', 'movements'],
    'Account Summary': ['accounts'],
    'Category Summary': ['spending'],
    'Payment Mode Summary': ['spending'],
    'Budget Summary': ['budgets'],
    'Plan Payments': ['plans'],
    Transfers: ['movements'],
    'Credit Card Payments': ['movements'],
    'CRDR Summary': ['crdr'],
    'Tax Summary': ['tax'],
    'Balance Audits': ['audits'],
    'Instructions Metadata': ['overview']
  };
  return (map[name] || ['overview']).some(include);
};

export function exportReportExcel(filename: string, data: ExportReportData) {
  const accountById = new Map(data.activeAccounts.map((account) => [account.id, account.name]));
  const sheets = [
    { name: 'Summary', xml: summarySheet(data) },
    { name: 'Transactions', xml: worksheet(['Date', 'Type', 'Category', 'Amount', 'Payment Mode', 'Account', 'Tax Tag', 'Note', 'Status', 'Transaction ID'], data.rangeTx.map((row) => [row.dateISO, row.type, row.category, row.amount, row.paymentMode, row.accountName || 'Unassigned', row.taxTag || '', row.note || '', row.status || 'active', row.id]), [14, 14, 20, 14, 18, 22, 22, 34, 14, 26]) },
    { name: 'Account Ledger', xml: worksheet(['Date', 'Account', 'Type', 'Direction', 'Amount', 'Related Account', 'Note', 'Reference', 'Ledger ID'], data.rangeLedger.map((row) => [row.dateISO, accountById.get(row.accountId) || row.accountId, row.type, row.direction, row.amount, accountById.get(row.relatedAccountId || '') || row.relatedAccountId || '', row.note || '', row.transactionId || row.transferId || row.cardPaymentId || row.planPaymentId || '', row.id]), [14, 24, 22, 14, 14, 24, 34, 28, 28]) },
    { name: 'Account Summary', xml: worksheet(['Account Name', 'Account Type', 'Opening Balance', 'Current Balance', 'Credit Limit', 'Available Limit', 'Outstanding', 'Status'], data.activeAccounts.map((account) => Object.values(accountSummaryRow(account))), [24, 18, 18, 18, 18, 18, 18, 14]) },
    { name: 'Category Summary', xml: worksheet(['Category', 'Expense'], data.categoryExpense.map(([name, amount]) => [name, amount]), [28, 16]) },
    { name: 'Payment Mode Summary', xml: worksheet(['Payment Mode', 'Expense'], data.paymentModeExpense.map(([name, amount]) => [name, amount]), [24, 16]) },
    { name: 'Budget Summary', xml: worksheet(['Month/Range', 'Category', 'Limit', 'Spent', 'Remaining', 'Usage %', 'Status'], data.budgetUsage.map((row) => [row.monthKey || `${data.fromDate} to ${data.toDate}`, row.category, row.limitAmount, row.spent, row.remaining, row.usagePct, row.state]), [18, 24, 16, 16, 16, 12, 16]) },
    { name: 'Plan Payments', xml: worksheet(['Date', 'Plan Name', 'Amount', 'Payment Mode', 'Paid From Account', 'Note'], data.rangePayments.map((row) => [row.paidDate, row.title || row.planName || 'Paid plan', row.amount, row.paymentMode, row.paidFromAccountName || 'Unassigned', row.note || row.remark || '']), [14, 28, 14, 18, 24, 34]) },
    { name: 'Transfers', xml: worksheet(['Date', 'Account', 'Amount', 'Note', 'Reference'], data.transfers.map((row) => [row.dateISO, accountById.get(row.accountId) || row.accountId, row.amount, row.note || '', row.transferId || '']), [14, 24, 14, 34, 28]) },
    { name: 'Credit Card Payments', xml: worksheet(['Date', 'Account', 'Amount', 'Note', 'Reference'], data.cardPayments.map((row) => [row.dateISO, accountById.get(row.accountId) || row.accountId, row.amount, row.note || '', row.cardPaymentId || '']), [14, 24, 14, 34, 28]) },
    { name: 'CRDR Summary', xml: worksheet(['Metric', 'Amount'], [['Credit', data.cr], ['Debit', data.dr], ['Net CR/DR', data.crdrBalance]], [20, 16]) },
    { name: 'Tax Summary', xml: worksheet(['Tax Tag', 'Income', 'Expense', 'Investment', 'Net', 'Transaction Count'], taxSummaryRows(data), [28, 16, 16, 16, 16, 18]) },
    { name: 'Balance Audits', xml: worksheet(['Audit Date', 'Account', 'System Balance', 'Actual Balance', 'Difference', 'System Available Limit', 'Actual Available Limit', 'Available Difference', 'Note'], data.rangeAudits.map((row) => [row.dateISO, row.accountName, row.systemBalance, row.actualBalance, row.difference, row.systemAvailableLimit, row.actualAvailableLimit, row.availableLimitDifference, row.note || '']), [14, 24, 18, 18, 16, 22, 22, 22, 34]) },
    { name: 'Instructions Metadata', xml: worksheet(['Item', 'Value'], [['Generated by', 'PFS Personal Finance System'], ['Date range', `${data.fromDate} to ${data.toDate}`], ['Account filter', data.accountFilter], ['Export timestamp', data.generatedAt], ['Note', 'This report is for personal finance tracking and ITR-support preparation.'], ['Disclaimer', 'It is not an official tax filing document. Verify with statements before filing.']], [28, 82]) }
  ].filter((sheet) => keepSheet(data, sheet.name));
  downloadBlob(filename, makeZip(workbookFiles(sheets, styles)));
}

const table = (title: string, headers: string[], rows: Array<Array<string | number>>, maxRows = 16): PdfTable => ({ title, headers, rows: rows.slice(0, maxRows) });

export function exportReportPdf(filename: string, data: ExportReportData) {
  const include = sectionFilter(data);
  const sections = [
    { section: 'overview', table: table('Executive Summary', ['Metric', 'Value'], [['Today Spend', inr(data.todaySpend)], ['Plan Payments', inr(data.paidTotal)], ['CR/DR Net', inr(data.crdrBalance)]]) },
    { section: 'accounts', table: table('Account Summary', ['Account', 'Type', 'Current', 'Available/Outstanding'], data.activeAccounts.map((account) => [account.name, account.type.replace('_', ' '), inr(account.currentBalance), account.type === 'credit_card' ? `Available ${inr(account.availableLimit || 0)}` : ''])) },
    { section: 'spending', table: table('Category-wise Expense', ['Category', 'Expense'], data.categoryExpense.map(([name, amount]) => [name, inr(amount)])) },
    { section: 'spending', table: table('Payment-mode-wise Expense', ['Payment Mode', 'Expense'], data.paymentModeExpense.map(([name, amount]) => [name, inr(amount)])) },
    { section: 'budgets', table: table('Budget Summary', ['Category', 'Limit', 'Spent', 'Usage', 'Status'], data.budgetUsage.map((row) => [row.category, inr(row.limitAmount), inr(row.spent), `${row.usagePct}%`, row.state])) },
    { section: 'movements', table: table('Transfers', ['Date', 'Amount', 'Note'], data.transfers.map((row) => [displayDate(row.dateISO), inr(row.amount), row.note || 'Transfer'])) },
    { section: 'movements', table: table('Credit Card Payments', ['Date', 'Amount', 'Note'], data.cardPayments.map((row) => [displayDate(row.dateISO), inr(row.amount), row.note || 'Card payment'])) },
    { section: 'plans', table: table('Plan Payments', ['Date', 'Plan', 'Amount', 'Paid From'], data.rangePayments.map((row) => [displayDate(row.paidDate), row.title || row.planName || 'Paid plan', inr(row.amount), row.paidFromAccountName || 'Unassigned'])) },
    { section: 'crdr', table: table('CR/DR Summary', ['Metric', 'Amount'], [['Credit', inr(data.cr)], ['Debit', inr(data.dr)], ['Net', inr(data.crdrBalance)]]) },
    { section: 'tax', table: table('Tax Summary', ['Tax Tag', 'Income', 'Expense', 'Investment', 'Net'], taxSummaryRows(data).map((row) => [row[0], inr(Number(row[1])), inr(Number(row[2])), inr(Number(row[3])), inr(Number(row[4]))])) },
    { section: 'transactions', table: table('Top Transactions', ['Date', 'Category', 'Payment', 'Amount'], data.topExpenses.map((row) => [displayDate(row.dateISO), row.category, row.paymentMode, inr(row.amount)]), 10) }
  ].filter((item) => include(item.section)).map((item) => item.table);
  const pdf: FinancePdfInput = {
    title: 'PFS Finance Report',
    subtitle: 'Personal Finance System',
    generatedAt: data.generatedAt,
    meta: [['Date range', `${displayDate(data.fromDate)} to ${displayDate(data.toDate)}`], ['Account filter', data.accountFilter], ['Generated', data.generatedAt], ['App', 'PFS']],
    kpis: [
      { label: 'Income', value: inr(data.income), tone: 'green' },
      { label: 'Expense', value: inr(data.expense), tone: 'red' },
      { label: 'Investment', value: inr(data.investment), tone: 'blue' },
      { label: 'Net Balance', value: inr(data.balance), tone: data.balance >= 0 ? 'green' : 'red' }
    ],
    sections
  };
  downloadFinancePdf(filename, pdf);
}
